import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { api } from '../api/endpoints';
import type { CustomerFilter, CustomerList } from '../api/types';
import { useDataVersion } from '../hooks/useDataVersion';
import {
  creditLabel,
  formatAgo,
  formatBalance,
  formatMinutes,
  formatDate,
  formatMoney,
  owedAfterCredit,
} from '../lib/format';
import { useToast } from '../components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Input,
  Modal,
  Spinner,
} from '../components/ui';

function NewCustomerDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  // Station sign-in. Both optional: blank means a username made from the
  // name and the default password, 1234.
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // A phone clash is the normal "they've been here before" case, so the 409
  // comes back with the existing account and we offer it as one click.
  const [clash, setClash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setClash(null);
    try {
      const created = await api.customers.create({
        name: name.trim(),
        phone: phone.trim(),
        notes: notes.trim() || undefined,
        username: username.trim() || undefined,
        password: password || undefined,
      });
      toast.success(
        created.default_password
          ? `${created.name}'s account is ready. Station sign-in: ${created.username} / ${created.default_password}.`
          : `${created.name}'s account is ready. Station sign-in: ${created.username}.`,
      );
      navigate(`/customers/${created.customer_id}`);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      const existing = (apiError.data as { customer_id?: string })?.customer_id;
      if (apiError.status === 409 && existing) setClash(existing);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New customer" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <ErrorNote>
            <p>{error}</p>
            {clash && (
              <Button
                variant="secondary"
                className="mt-3"
                onClick={() => navigate(`/customers/${clash}`)}
              >
                Open that account instead
              </Button>
            )}
          </ErrorNote>
        )}

        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          autoFocus
        />
        <Input
          label="Phone"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          inputMode="numeric"
          hint="10 to 15 digits. This is the account key staff search by."
          required
        />
        <Input
          label="Notes (optional)"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <Input
          label="Station username (optional)"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="off"
          hint="Left blank, one is made from the name."
        />
        <Input
          label="Station password (optional)"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          minLength={4}
          maxLength={72}
          hint="Left blank, it is 1234. They can change it on the lock screen."
        />

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </Button>
      </form>
    </Modal>
  );
}

// Most recently touched first, a page at a time. The backend caps a page at
// 100 rows.
const PAGE_SIZE = 50;

// The chips above the list. `tone` colours the count so the ones that need
// action (money owed, time about to lapse) stand out even when not selected.
const FILTERS: { key: CustomerFilter; label: string; tone: string }[] = [
  { key: 'all', label: 'All', tone: 'text-slate-400' },
  { key: 'playing', label: 'Playing now', tone: 'text-sky-300' },
  { key: 'due', label: 'Due', tone: 'text-amber-300' },
  { key: 'overdue', label: 'Overdue', tone: 'text-rose-300' },
  { key: 'credit', label: 'Credit', tone: 'text-sky-300' },
  { key: 'hours', label: 'Hours left', tone: 'text-emerald-300' },
  { key: 'expiring', label: 'Expiring in 7 days', tone: 'text-amber-300' },
  { key: 'new', label: 'New this week', tone: 'text-slate-300' },
  { key: 'inactive', label: 'Inactive', tone: 'text-slate-400' },
];

const isFilter = (value: string | null): value is CustomerFilter =>
  FILTERS.some((item) => item.key === value);

function FilterChips({
  value,
  counts,
  onChange,
}: {
  value: CustomerFilter;
  counts: CustomerList['filter_counts'] | undefined;
  onChange: (next: CustomerFilter) => void;
}) {
  return (
    // One scrolling row on phones rather than wrapping into a wall of chips.
    <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
      {FILTERS.map((item) => {
        const active = item.key === value;
        const count = counts?.[item.key];
        return (
          <button
            key={item.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(item.key)}
            className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition-colors ${
              active
                ? 'border-sky-500 bg-sky-500/20 text-sky-100'
                : count === 0
                  ? 'border-slate-800 bg-transparent text-slate-500 hover:bg-slate-900'
                  : 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
            }`}
          >
            {item.label}
            {count !== undefined && (
              <span
                className={`rounded-full bg-slate-950/60 px-2 py-0.5 text-xs tabular-nums ${
                  active ? 'text-sky-200' : count === 0 ? 'text-slate-600' : item.tone
                }`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function CustomersPage() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  // What is actually sent. Typing waits a beat so each key press is not a
  // request; a chip click goes out at once.
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<CustomerList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(params.get('new') === '1');
  // In the URL, so coming back from a customer's page keeps the filter.
  const filterParam = params.get('filter');
  const filter: CustomerFilter = isFilter(filterParam) ? filterParam : 'all';
  // The rows badge what each customer owes; refetch when any write lands.
  const version = useDataVersion();

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      try {
        const result = await api.customers.list(
          query,
          PAGE_SIZE,
          page * PAGE_SIZE,
          controller.signal,
          filter,
        );
        // Past the end (the list shrank since the page was opened): step back
        // rather than telling staff nobody matches.
        if (page > 0 && result.customers.length === 0) {
          setPage(Math.max(0, Math.ceil(result.total_count / PAGE_SIZE) - 1));
          return;
        }
        // Tagged with the filter that was asked for, which is what the
        // staleness check below compares against.
        setData({ ...result, filter });
        setError(null);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') setError((err as Error).message);
      } finally {
        // A request cancelled by a newer one must not end the newer one's
        // loading state.
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void load();
    return () => controller.abort();
  }, [query, page, filter, version]);

  // Rows from a different filter are never shown under the new chip: until
  // the new list arrives the page shows a spinner, so a slow reply cannot
  // look like the filter did nothing. (A changed search keeps the old rows
  // up while typing, to avoid flicker.)
  const shown = data && data.filter === filter ? data : null;

  const changeFilter = (next: CustomerFilter) => {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next === 'all') updated.delete('filter');
        else updated.set('filter', next);
        return updated;
      },
      { replace: true },
    );
    setPage(0);
  };

  const closeCreate = () => {
    setCreating(false);
    if (params.get('new')) {
      params.delete('new');
      setParams(params, { replace: true });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Customers</h1>
        <Button onClick={() => setCreating(true)}>New customer</Button>
      </div>

      <Input
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          // A new search starts from its own first page.
          setPage(0);
        }}
        placeholder="Search by phone, name or username"
        inputMode="search"
        autoFocus
      />

      <FilterChips value={filter} counts={data?.filter_counts} onChange={changeFilter} />

      {error && <ErrorNote>{error}</ErrorNote>}

      {!shown ? (
        !error && <Spinner />
      ) : shown.customers.length === 0 && filter !== 'all' ? (
        <EmptyState title="No customers in this filter.">
          {search.trim() ? 'Nobody matching that search is in this filter.' : 'Nothing to chase here right now.'}
          <div className="mt-3">
            <Button variant="secondary" onClick={() => changeFilter('all')}>
              Show all customers
            </Button>
          </div>
        </EmptyState>
      ) : shown.customers.length === 0 ? (
        <EmptyState title="Nobody matches that.">
          A first visit is the normal reason for this, not an error — create the
          account and sell them a pack.
          <div className="mt-3">
            <Button onClick={() => setCreating(true)}>Create customer</Button>
          </div>
        </EmptyState>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {shown.customers.map((row) => (
            <li key={row.customer_id}>
              <Link
                to={`/customers/${row.customer_id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-slate-900"
              >
                <div>
                  <p className="font-medium text-slate-100">
                    {row.name}
                    {!row.is_active && (
                      <span className="ml-2 text-xs text-slate-500">inactive</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {row.phone}
                    {row.station_login && row.username && (
                      <span className="font-mono"> · {row.username}</span>
                    )}
                    {row.seconds_played !== undefined && (
                      <span> · played {formatMinutes(Math.floor(row.seconds_played / 60))}</span>
                    )}
                    <span> · updated {formatAgo(row.last_activity_at)}</span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {row.playing && <Badge tone="sky">Playing now</Badge>}
                  {row.expiring_soon && <Badge tone="amber">Time expiring soon</Badge>}
                  {/* An unpaid tab is flagged where the name is searched, so
                      it is seen before the customer is seated or walks out. */}
                  {owedAfterCredit(row.fnb_due_amount, row.credit_balance) > 0 &&
                    (row.tab_open_until && new Date(row.tab_open_until).getTime() < Date.now() ? (
                      <Badge tone="red">
                        Overdue {formatMoney(owedAfterCredit(row.fnb_due_amount, row.credit_balance))} · {formatDate(row.tab_open_until)}
                      </Badge>
                    ) : (
                      <Badge tone="amber">Owes {formatMoney(owedAfterCredit(row.fnb_due_amount, row.credit_balance))}</Badge>
                    ))}
                  {row.credit_balance > 0 && (
                    <Badge tone="sky">{creditLabel(row.credit_balance, row.fnb_due_amount)}</Badge>
                  )}
                  <Badge tone={row.general_minutes > 0 ? 'green' : 'slate'}>
                    PC {formatBalance(row.general_seconds_remaining ?? row.general_minutes * 60)}
                  </Badge>
                  <Badge tone={row.simulator_minutes > 0 ? 'green' : 'slate'}>
                    Sim {formatBalance(row.simulator_seconds_remaining ?? row.simulator_minutes * 60)}
                  </Badge>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {shown && shown.total_count > PAGE_SIZE && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + shown.customers.length} of{' '}
            {shown.total_count}
            {loading && ' · loading…'}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={page === 0 || loading}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={(page + 1) * PAGE_SIZE >= shown.total_count || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {creating && <NewCustomerDialog onClose={closeCreate} />}
    </div>
  );
}
